"use client";
import * as React from "react";
import { useTooltip } from "./contexts/TooltipContext";
import { SortOption } from "./types";

interface SearchFiltersProps {
  searchTerm: string;
  selectedSector: string;
  selectedCareerCluster: string;
  sectors: string[];
  careerClusters: string[];
  sortBy: SortOption;
  onSearchChange: (value: string) => void;
  onSectorChange: (sector: string) => void;
  onCareerClusterChange: (careerCluster: string) => void;
  onSortChange: (sortBy: SortOption) => void;
}

export function SearchFilters({
  searchTerm,
  selectedSector,
  selectedCareerCluster,
  sectors,
  careerClusters,
  sortBy,
  onSearchChange,
  onSectorChange,
  onCareerClusterChange,
  onSortChange,
  isGrowthSortActive,
  minCurrentJobsThreshold,
  includeSmallGrowthOccupations,
  canAdjustMinCurrentJobs,
  onIncludeSmallGrowthOccupationsChange,
  isSearching = false,
}: SearchFiltersProps & {
  isSearching?: boolean;
  isGrowthSortActive: boolean;
  minCurrentJobsThreshold: number;
  includeSmallGrowthOccupations: boolean;
  canAdjustMinCurrentJobs: boolean;
  onIncludeSmallGrowthOccupationsChange: (include: boolean) => void;
}) {
  // Tooltip context for global tooltip state
  const { setIsTooltipOpen } = useTooltip();
  
  const [showFocusIndustryTooltip, setShowFocusIndustryTooltip] = React.useState(false);

  // Close tooltip when clicking outside (with delay to prevent immediate close)
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking the info button or the tooltip itself
      if (target.closest('[aria-label="Why these are Sumner County TN Focus Industries"]') || 
          target.closest('.focus-industry-tooltip')) {
        return;
      }
      setShowFocusIndustryTooltip(false);
      setIsTooltipOpen(false);
    };
    if (showFocusIndustryTooltip) {
      // Add small delay to prevent immediate trigger
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showFocusIndustryTooltip]);

  const sortOptions = [
    { value: 'default' as SortOption, label: 'Default Order', icon: '🎯' },
    { value: 'title-asc' as SortOption, label: 'A to Z', icon: '🔤' },
    { value: 'title-desc' as SortOption, label: 'Z to A', icon: '🔤' },
    { value: 'salary-desc' as SortOption, label: 'Highest Salary', icon: '💰' },
    { value: 'salary-asc' as SortOption, label: 'Lowest Salary', icon: '💰' },
    { value: 'hourly-desc' as SortOption, label: 'Highest Hourly Pay', icon: '⏰' },
    { value: 'hourly-asc' as SortOption, label: 'Lowest Hourly Pay', icon: '⏰' },
    { value: 'employment-desc' as SortOption, label: 'Highest Current Employment', icon: '👥' },
    { value: 'employment-asc' as SortOption, label: 'Lowest Current Employment', icon: '👥' },
    { value: 'job-postings-desc' as SortOption, label: 'Most Job Postings', icon: '📋' },
    { value: 'job-postings-asc' as SortOption, label: 'Least Job Postings', icon: '📋' },
    { value: 'growth-trend-desc' as SortOption, label: 'Highest Growth Trend', icon: '📈' },
    { value: 'growth-trend-asc' as SortOption, label: 'Lowest Growth Trend', icon: '📈' },
  ];

  const getCurrentSortLabel = () => {
    const option = sortOptions.find(opt => opt.value === sortBy);
    return option ? option.label : 'Default Order';
  };

  const getCurrentSortIcon = () => {
    const option = sortOptions.find(opt => opt.value === sortBy);
    return option ? option.icon : '🎯';
  };

  return (
    <div className="p-2 sm:p-6 mb-3 sm:mb-6 bg-blue-50 rounded-lg sm:rounded-2xl border border-gray-200 border-solid search-filters">
      <h2 className="mx-0 mt-0 mb-2 sm:mb-4 text-lg sm:text-3xl font-semibold text-zinc-700">
        Explore Career Opportunities
      </h2>
      
      {/* Current Sort Indicator */}
      {sortBy !== 'default' && (
        <div className="mb-4 p-3 bg-white rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <span className="text-base">{getCurrentSortIcon()}</span>
            <span className="font-medium">Sorted by: {getCurrentSortLabel()}</span>
            <button 
              onClick={() => onSortChange('default')}
              className="ml-2 text-xs text-red-600 hover:text-red-800 underline"
            >
              Clear Sort
            </button>
          </div>
        </div>
      )}
      
      {/* Search Input */}
      <div className="mb-3 sm:mb-6">
        <label
          htmlFor="career-search"
          className="block mb-1 sm:mb-3 text-xs sm:text-base font-semibold text-zinc-700"
        >
          Search Careers
        </label>
        <div className="flex relative items-center">
          {isSearching ? (
            <div className="absolute left-3 sm:left-4 w-3 h-3 sm:w-5 sm:h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute left-3 sm:left-4 opacity-50 pointer-events-none text-zinc-700 z-[1] sm:w-5 sm:h-5"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="m21 21-4.35-4.35"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          <input
            className="py-2 sm:py-4 pr-2 sm:pr-4 pl-8 sm:pl-12 w-full text-xs sm:text-base bg-white rounded-md sm:rounded-xl border-2 border-gray-200 border-solid transition-all duration-[0.3s] ease-[ease] shadow-[0_2px_4px_rgba(0,0,0,0.05)] focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            id="career-search"
            type="text"
            placeholder="Search by career title, cluster, or focus area..."
            value={searchTerm}
            onChange={(event) =>
              onSearchChange((event.target as HTMLInputElement).value)
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
              }
            }}
          />
        </div>
      </div>

      {/* Filter and Sort Controls */}
      <div className="grid gap-2 sm:gap-4 grid-cols-1 sm:grid-cols-3 filter-dropdowns">
        {/* Sector Filter */}
        <div>
          <label
            htmlFor="sector-filter"
            className="block mb-1 sm:mb-3 text-xs sm:text-base font-semibold text-zinc-700"
            style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <span>Sumner County, TN Focus Industry</span>
            <button
              type="button"
              className="inline-flex items-center justify-center cursor-pointer border-0 bg-transparent p-0"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowFocusIndustryTooltip(!showFocusIndustryTooltip);
                setIsTooltipOpen(!showFocusIndustryTooltip);
              }}
              onMouseEnter={() => {
                if (!showFocusIndustryTooltip) {
                  setShowFocusIndustryTooltip(true);
                  setIsTooltipOpen(true);
                }
              }}
              aria-label="Why these are Sumner County TN Focus Industries"
              tabIndex={0}
            >
              <svg style={{ width: '16px', height: '16px', color: '#d5a32a' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </button>
            {showFocusIndustryTooltip && (
              <div 
                className="focus-industry-tooltip absolute left-0 top-full mt-2 z-[99999] w-80 max-w-[calc(100vw-2rem)] p-4 bg-white border-2 border-[#d5a32a] rounded-lg shadow-xl text-sm text-gray-700"
                style={{ whiteSpace: 'normal' }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <h4 style={{ fontWeight: 'bold', color: '#1b2c5a', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Why these are "Sumner County, TN Focus Industries"
                </h4>
                <p style={{ margin: 0, lineHeight: '1.5', fontSize: '0.85rem' }}>
                  These sectors are prioritized by Forward Sumner's Workforce Cabinet, a cross-sector group of employers, educators, and public partners that co-designs local talent pipelines.
                </p>
                <div className="absolute -top-2 left-4 w-3 h-3 bg-white border-t-2 border-l-2 border-[#d5a32a] transform rotate-45"></div>
              </div>
            )}
          </label>
          <select
            id="sector-filter"
            className="py-2 sm:py-4 px-2 sm:px-4 w-full text-xs sm:text-base bg-white rounded-md sm:rounded-xl border-2 border-gray-200 border-solid transition-all duration-[0.3s] ease-[ease] shadow-[0_2px_4px_rgba(0,0,0,0.05)] focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            value={selectedSector}
            onChange={(event) => onSectorChange(event.target.value)}
          >
            {sectors.map((sector) => (
              <option key={sector} value={sector}>
                {sector === "all" ? "All Sectors" : sector}
              </option>
            ))}
          </select>
        </div>

        {/* Career Cluster Filter */}
        <div>
          <label
            htmlFor="career-cluster-filter"
            className="block mb-1 sm:mb-3 text-xs sm:text-base font-semibold text-zinc-700 cursor-pointer"
            title="CTE = Career & Technical Education. Learn more on SCS CTE site."
            onClick={() => window.open('https://www.sumnerschools.org/parents/career-and-technical-education', '_blank')}
          >
            Filter by CTE Cluster
          </label>
          <select
            id="career-cluster-filter"
            className="py-2 sm:py-4 px-2 sm:px-4 w-full text-xs sm:text-base bg-white rounded-md sm:rounded-xl border-2 border-gray-200 border-solid transition-all duration-[0.3s] ease-[ease] shadow-[0_2px_4px_rgba(0,0,0,0.05)] focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            value={selectedCareerCluster}
            onChange={(event) => onCareerClusterChange(event.target.value)}
          >
            {careerClusters.map((cluster) => (
              <option key={cluster} value={cluster}>
                {cluster === "all" ? "All Career Clusters" : cluster}
              </option>
            ))}
          </select>
        </div>

        {/* Sort By */}
        <div>
          <label
            htmlFor="sort-filter"
            className="block mb-1 sm:mb-3 text-xs sm:text-base font-semibold text-zinc-700"
          >
            Sort by
          </label>
          <select
            id="sort-filter"
            className="py-2 sm:py-4 px-2 sm:px-4 w-full text-xs sm:text-base bg-white rounded-md sm:rounded-xl border-2 border-gray-200 border-solid transition-all duration-[0.3s] ease-[ease] shadow-[0_2px_4px_rgba(0,0,0,0.05)] focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            value={sortBy}
            onChange={(event) => onSortChange(event.target.value as SortOption)}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.icon} {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isGrowthSortActive && (
        <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-white rounded-lg border border-blue-100 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
          <p className="m-0 mb-2 text-xs sm:text-sm text-zinc-700">
            {includeSmallGrowthOccupations
              ? "Showing all occupations, including those with fewer than the default minimum current jobs."
              : `Only showing occupations with ≥${minCurrentJobsThreshold} current jobs so ultra-small roles don't dominate this Growth Trend list.`}
          </p>
          <div className="flex flex-col gap-1">
            <label
              className={`flex items-center gap-2 text-xs sm:text-sm ${
                canAdjustMinCurrentJobs ? "text-zinc-700" : "text-gray-400"
              }`}
            >
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                checked={includeSmallGrowthOccupations}
                onChange={(event) => onIncludeSmallGrowthOccupationsChange(event.target.checked)}
                disabled={!canAdjustMinCurrentJobs}
              />
              <span>Include occupations under {minCurrentJobsThreshold} current jobs</span>
            </label>
            {!canAdjustMinCurrentJobs && (
              <p className="m-0 text-[11px] sm:text-xs text-gray-400">
                Filter by a CTE cluster to enable this toggle.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
